import { type ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { PublicLayout } from '@/components/layouts/public-layout'
import { Button } from '@/components/ui/button'

const SERVICES = [
  {
    title: 'Chef privé',
    img: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=900&q=80',
  },
  {
    title: 'Conciergerie villa',
    img: 'https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=900&q=80',
  },
  {
    title: 'Location bateau',
    img: 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d13?auto=format&fit=crop&w=900&q=80',
  },
  {
    title: 'Wellness & soins',
    img: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=900&q=80',
  },
]

const PRODUCTS = [
  {
    title: 'Champagnes rares',
    img: 'https://images.unsplash.com/photo-1547595628-c61a29f496f0?auto=format&fit=crop&w=700&q=80',
  },
  {
    title: 'Caviar français',
    img: 'https://images.unsplash.com/photo-1559588482-69774768212a?auto=format&fit=crop&w=700&q=80',
  },
  {
    title: 'Truffe Périgord',
    img: 'https://images.unsplash.com/photo-1597771125516-4eddaf867446?auto=format&fit=crop&w=700&q=80',
  },
  {
    title: 'Cigares de prestige',
    img: 'https://images.unsplash.com/photo-1514514589924-94eda1732498?auto=format&fit=crop&w=700&q=80',
  },
]

export function LandingPage(): ReactElement {
  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1569263979104-865ab7cd8d13?auto=format&fit=crop&w=2400&q=80"
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/85 via-primary/60 to-background" />
        </div>
        <div className="mx-auto flex min-h-[78vh] max-w-7xl flex-col justify-center px-6 py-32 text-primary-foreground">
          <p className="mb-4 text-xs uppercase tracking-[0.4em] text-accent">
            L'art du provisioning
          </p>
          <h1 className="font-display max-w-3xl text-5xl leading-tight md:text-7xl">
            Rendre l'impossible
            <br />
            <span className="text-accent">possible.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base text-primary-foreground/80">
            Conciergerie ultra-personnalisée pour yachts, villas et clients exigeants. De Monaco à
            Saint-Tropez, nous orchestrons l'exception.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Button size="lg" className="bg-accent text-primary hover:bg-accent/90">
              <Link to="/signup">Devenir client</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
            >
              <Link to="#services">Découvrir nos services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section id="vision" className="mx-auto max-w-4xl px-6 py-24 text-center">
        <p className="text-xs uppercase tracking-[0.4em] text-accent">
          <span className="gold-rule" />
          Notre vision
          <span className="gold-rule" />
        </p>
        <h2 className="font-display mt-6 text-4xl leading-tight md:text-5xl">
          Chez MyPivot, tout est pensé sur mesure.
        </h2>
        <p className="mt-6 text-lg text-muted-foreground">
          Choisissez un service — nous orchestrons le reste. Chefs étoilés, équipages, floraisons
          signature, artisans rares. Une seule promesse&nbsp;: l'exigence.
        </p>
      </section>

      {/* Services */}
      <section id="services" className="bg-muted/40 py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 flex items-end justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-accent">Conciergerie</p>
              <h2 className="font-display mt-3 text-4xl">Services signature</h2>
            </div>
            <Link to="/signup" className="text-sm text-muted-foreground hover:text-foreground">
              Voir le catalogue complet →
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s) => (
              <article
                key={s.title}
                className="group overflow-hidden rounded-lg border border-border/60 bg-card"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-display text-xl">{s.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Sur demande, sur mesure.</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="produits" className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 text-center">
            <p className="text-xs uppercase tracking-[0.4em] text-accent">Produits</p>
            <h2 className="font-display mt-3 text-4xl">Sélection rare</h2>
            <p className="mt-3 text-muted-foreground">Chaque sélection est faite avec exigence.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PRODUCTS.map((p) => (
              <div key={p.title} className="group">
                <div className="aspect-square overflow-hidden rounded-md">
                  <img
                    src={p.img}
                    alt={p.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <h3 className="mt-3 text-sm font-medium">{p.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-4xl flex-col items-center px-6 py-20 text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-accent">Sur invitation</p>
          <h2 className="font-display mt-4 text-4xl md:text-5xl">
            Vivez l'exception sans friction.
          </h2>
          <p className="mt-4 max-w-2xl text-primary-foreground/70">
            Créez votre espace privé et soumettez vos demandes. Un devis sur mesure vous est
            transmis sous 24 h.
          </p>
          <Button
            size="lg"
            className="mt-8 bg-accent text-primary hover:bg-accent/90"
            onClick={() => {
              location.assign('/signup')
            }}
          >
            Créer mon espace client
          </Button>
        </div>
      </section>
    </PublicLayout>
  )
}
